import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const GymAnnouncements = () => {
  const navigate = useNavigate();
  const { 
    user, 
    currentTenant, 
    isAuthenticated, 
    loading: authLoading,
    notifications,
    unreadCount,
    markNotificationAsRead,
    markAllNotificationsAsRead
  } = useAuth();
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const filteredNotifications = notifications?.filter(notification => {
    if (filter === 'unread') return !notification?.is_read;
    if (filter === 'read') return notification?.is_read;
    return true;
  });

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'error': return 'XCircle';
      case 'info':
      default: return 'Info';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      case 'info':
      default: return 'text-primary';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read) {
      await markNotificationAsRead(notification?.id);
    }
    if (notification?.action_url) {
      navigate(notification?.action_url);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 md:py-8 pb-24 md:pb-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                Notifications
              </h1>
              <p className="text-muted-foreground">
                {currentTenant?.name || 'Stay updated with your gym'}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={markAllNotificationsAsRead}
                className="hidden md:flex"
              >
                <Icon name="CheckCheck" size={16} className="mr-2" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 border-b border-border">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === 'all' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              All ({notifications?.length || 0})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === 'unread' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Unread ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === 'read' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Read ({(notifications?.length || 0) - unreadCount})
            </button>
          </div>
        </div>

        {/* Mobile Mark All Read Button */}
        {unreadCount > 0 && (
          <div className="md:hidden mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={markAllNotificationsAsRead}
              className="w-full"
            >
              <Icon name="CheckCheck" size={16} className="mr-2" />
              Mark all as read
            </Button>
          </div>
        )}

        {/* Notifications List */}
        <div className="space-y-3">
          {filteredNotifications?.length === 0 ? (
            <div className="bg-card rounded-xl p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="Bell" size={32} className="text-muted-foreground" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No notifications
              </h3>
              <p className="text-muted-foreground">
                {filter === 'unread' ?'You\'re all caught up!' 
                  : filter === 'read' ?'No read notifications yet' :'You have no notifications at this time'}
              </p>
            </div>
          ) : (
            filteredNotifications?.map((notification) => (
              <div
                key={notification?.id}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-card rounded-xl p-4 md:p-5 transition-all cursor-pointer hover:shadow-md ${
                  !notification?.is_read ? 'border-l-4 border-primary' : ''
                }`}
              >
                <div className="flex gap-4">
                  <div className={`flex-shrink-0 ${getNotificationColor(notification?.type)}`}>
                    <Icon name={getNotificationIcon(notification?.type)} size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">
                        {notification?.title}
                      </h3>
                      {!notification?.is_read && (
                        <span className="flex-shrink-0 h-2 w-2 rounded-full bg-primary"></span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {notification?.message}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {formatDate(notification?.created_at)}
                      </span>
                      {notification?.is_read && notification?.read_at && (
                        <span className="flex items-center gap-1">
                          <Icon name="CheckCircle" size={12} />
                          Read
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default GymAnnouncements;